import { useMemo } from 'react'

import estilos from './MiDia.module.css'

/**
 * EL DÍA DEL TÉCNICO, DE UN VISTAZO.
 *
 * La pantalla era una pila de tarjetas blancas del mismo peso: el aviso de
 * notificaciones, el mapa, cada trabajo. Sin un punto de entrada, lo primero
 * que hace el ojo es leerlo todo, y quien la abre a las siete de la mañana con
 * una mano en el volante necesita lo contrario.
 *
 * Es el mismo recurso que ancla el tablero de gerencia —un panel oscuro, UNO
 * solo, dentro de una página clara— y por eso se parecen: no es que se haya
 * copiado el color, es que resuelve el mismo problema. Si mañana hay dos
 * paneles oscuros aquí, no hay ninguno.
 *
 * Y dice lo que se pregunta a esa hora, en ese orden: cuánto hay hoy, dónde
 * empieza, y qué está pasando ahora mismo.
 */

type Trabajo = {
  estado: string
  cliente: string | null
  horas_asignadas: number | null
  fecha_inicio_programada: string | null
  en_sitio?: boolean | null
  llegada?: string | null
  vencida?: boolean | null
}

function hora(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit' })
}

/**
 * Qué día es esa hora, dicho como se dice de viva voz: «hoy», «mañana» o, más
 * allá, «el martes 22». Una hora suelta —«a las 4:30»— no dice si es de esta
 * tarde o de pasado mañana, y ahí es donde alguien se planta un día antes.
 *
 * Se compara por el día del calendario (no por «faltan 24 horas»): a las 11 de
 * la noche, un trabajo de la 1 de la madrugada es «mañana», no «faltan 2 h».
 */
function cuandoDia(iso: string | null): string {
  if (!iso) return ''
  const clave = (ms: number) => new Date(ms).toLocaleDateString('en-CA') // YYYY-MM-DD, día local
  const objetivo = clave(new Date(iso).getTime())
  const hoy = Date.now()
  if (objetivo === clave(hoy)) return 'hoy'
  if (objetivo === clave(hoy + 86_400_000)) return 'mañana'
  return 'el ' + new Date(iso).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric' })
}

export function MiDia({ trabajos, nombre }: { trabajos: Trabajo[]; nombre: string }) {
  const dia = useMemo(() => {
    const vivos = trabajos.filter((t) => t.estado !== 'cerrada' && t.estado !== 'finalizada')
    const horas = vivos.reduce((s, t) => s + Number(t.horas_asignadas ?? 0), 0)

    // El que está pasando ahora manda sobre todo lo demás: si hay uno en sitio,
    // la pregunta ya no es «qué tengo hoy» sino «cuánto llevo aquí».
    const enSitio = vivos.find((t) => t.en_sitio || t.estado === 'en_ejecucion') ?? null

    const siguiente = vivos
      .filter((t) => t !== enSitio && t.fecha_inicio_programada)
      .sort((a, b) =>
        (a.fecha_inicio_programada ?? '').localeCompare(b.fecha_inicio_programada ?? ''),
      )[0] ?? null

    return {
      cuantos: vivos.length,
      horas,
      enSitio,
      siguiente,
      vencidas: vivos.filter((t) => t.vencida).length,
    }
  }, [trabajos])

  // Sin nada por hacer el panel no se esconde: cambia de contenido. Un bloque
  // que desaparece en cero deja la pantalla plana justo cuando hay que explicar
  // por qué está vacía.
  const despejado = dia.cuantos === 0

  return (
    <section className={`${estilos.panel} ${despejado ? estilos.despejado : ''}`}>
      <p className={estilos.saludo}>{nombre ? `Hola, ${nombre}` : 'Su día'}</p>

      {despejado ? (
        <>
          <p className={estilos.cifra}>Sin trabajo asignado</p>
          <p className={estilos.pie}>
            Cuando programación le asigne una orden, aparece aquí sola. Si pasa por una obra y ve
            algo, puede cotizarlo con el botón de abajo.
          </p>
        </>
      ) : (
        <>
          <p className={estilos.cifra}>
            {dia.cuantos}
            <span className={estilos.unidad}>
              {dia.cuantos === 1 ? ' trabajo hoy' : ' trabajos hoy'}
            </span>
          </p>

          <p className={estilos.pie}>
            {dia.horas > 0 && (
              <>
                <b>{dia.horas.toFixed(0)} h</b> programadas
              </>
            )}
            {dia.siguiente && (
              <>
                {dia.horas > 0 && ' · '}
                el siguiente {cuandoDia(dia.siguiente.fecha_inicio_programada)} a las{' '}
                <b>{hora(dia.siguiente.fecha_inicio_programada)}</b>
              </>
            )}
          </p>

          {/* Lo que está pasando ahora, en oro: en toda la aplicación el oro
              quiere decir «aquí hay algo que atender». */}
          {dia.enSitio && (
            <p className={estilos.ahora}>
              En sitio: <b>{dia.enSitio.cliente ?? 'sin cliente'}</b>
            </p>
          )}

          {dia.vencidas > 0 && (
            <p className={estilos.tarde}>
              {dia.vencidas === 1
                ? '1 trabajo se pasó de la hora'
                : `${dia.vencidas} trabajos se pasaron de la hora`}
            </p>
          )}
        </>
      )}
    </section>
  )
}
